import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import { useAssessmentByCaseNumber } from "@/hooks/queries/use-assessments";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import {
  type RecordingBusyReason,
  useRecordingBusy,
} from "@/hooks/use-recording-busy";
import {
  claimRecording,
  discardRecording,
  prepareRecording,
  releaseRecording,
} from "@/services/recordingOwnership";
import { callApi } from "@/utils/callApi";
import { showError } from "@/utils/notification";

import type { AssessmentFieldStatusSchema } from "@api/generated/types.gen";

import { ReactComponent as MicIcon } from "@assets/svg/mic-filled.svg";

import { ConfirmationModal } from "@components/ConfirmationModal";
import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";
import { Badge } from "@components/ui/badge";
import { Button } from "@components/ui/button";
import { Dialog, DialogContent } from "@components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { ASSESSMENT_STATUS } from "../constants/sections";
import { hasUnsentRecording } from "../hooks/unsentRecording";
import { NOOP_CALL_AUDIO_SHARE } from "../hooks/useCallAudioShare";
import { RecordingSession } from "../hooks/useRecordingSession";
import type { SessionMode } from "../types/sessionMode";
import { AudioRecorder } from "./AudioRecorder";
import { AudioUploader } from "./AudioUploader";
import type { SpeakerHint } from "./SpeakerChecklist";

type TranscriptField = AssessmentFieldStatusSchema | string | null | undefined;

const getFieldContent = (field: TranscriptField) => {
  if (!field) {
    return "";
  }

  if (typeof field === "string") {
    return field;
  }

  return field.content ?? "";
};

// Copy for why recording is blocked; null hides the pill and tooltip.
export const blockedReasonText = (reason: RecordingBusyReason): string | null => {
  if (reason === "processing") return "Transcription in progress";
  return reason ? "Recording or upload in another tab" : null;
};

type TranscribeAndUploadButtonsProps = {
  caseNumber?: string;
  selectedId?: string;
  showButtons?: boolean;
  /**
   * Shared recording session (MDRS-116). Owned by the page so the consent modal
   * (start) and the in-panel ScribeSession (live view) drive the same session.
   */
  session: RecordingSession;
  /**
   * "header" (default) renders the compact top-bar buttons; "empty-state"
   * renders the centered no-content panel — mic pill, copy, and larger CTAs —
   * reusing the same start/upload flow (MDRS-113).
   */
  variant?: "header" | "empty-state";
};

export const TranscribeAndUploadButtons = ({
  caseNumber,
  selectedId,
  showButtons,
  session,
  variant = "header",
}: TranscribeAndUploadButtonsProps) => {
  const isEmptyState = variant === "empty-state";
  const { data: currentAssessment, isLoading: isAssessmentLoading } =
    useAssessmentByCaseNumber({ caseNumber, enabled: !!caseNumber });
  const { data: caseData, isLoading: isCaseLoading } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  const dispatch = useDispatch();
  const {
    busy: browserBusy,
    reason: busyReason,
    replaceable,
  } = useRecordingBusy(caseNumber);
  const reservationRef = useRef<{
    expectedId: string | null;
    overwrite: boolean;
    // The in-progress transcription the user confirmed replacing, if any.
    replaceId: string | null;
  } | null>(null);

  const isLoading = isAssessmentLoading || isCaseLoading;
  const initialNotes = currentAssessment?.notes ?? "";

  // A stuck transcription the server lets a new recording replace doesn't block.
  const isTranscribing =
    currentAssessment?.status === ASSESSMENT_STATUS.TRANSCRIBING && !replaceable;

  // Determine if buttons should be shown: either explicitly set or when selectedId is "1"
  const shouldShowButtons = showButtons ?? selectedId === "1";

  // The consent modal is open from Start Session until the session actually
  // starts (or the user backs out). Once recording begins the LIVE view is in
  // the content panel (MDRS-116), so the modal closes.
  const [isConsentOpen, setIsConsentOpen] = useState(false);
  const [isOverwriteWarningOpen, setIsOverwriteWarningOpen] = useState(false);
  const [isReplacingPending, setIsReplacingPending] = useState(false);
  // Telehealth call-tab audio share (MDRS-268), owned by the root
  // RecordingHost so it survives scribe-tab navigation and so the live
  // ScribeSession view reads the SAME share instance — see useRecordingDock.
  const callAudioShare =
    useRecordingDock((s) => s.callAudioShare) ?? NOOP_CALL_AUDIO_SHARE;
  const confirmationResolverRef = useRef<((value: boolean) => void) | null>(null);

  const hasExistingFullTranscript = useMemo(() => {
    const content = getFieldContent(currentAssessment?.full_transcript).trim();

    if (!content) {
      return false;
    }

    return content.toLowerCase() !== "null";
  }, [currentAssessment?.full_transcript]);

  // A stopped session blocks only until the server has its recording.
  const summarizeStatus = useRecordingDock((s) => s.summarize.status);
  const sessionInProgress =
    session.isConnecting ||
    session.isActive ||
    (session.isStopped && summarizeStatus !== "idle");

  // This tab's own session already labels itself "Session in progress".
  const blockedReason = sessionInProgress
    ? null
    : (blockedReasonText(busyReason) ??
      (isTranscribing ? blockedReasonText("processing") : null));

  const resolvePendingConfirmation = useCallback((value: boolean) => {
    if (confirmationResolverRef.current) {
      confirmationResolverRef.current(value);
      confirmationResolverRef.current = null;
    }
  }, []);

  const requestOverwriteConfirmation = useCallback(async () => {
    let state: {
      busy: boolean;
      replaceable?: boolean;
      submission_id: string | null;
    };
    try {
      state = await callApi(`transcription/submissions/state/${caseNumber}`, "GET");
    } catch {
      showError("Couldn't check this case's recording status. Try again.", dispatch);
      return false;
    }
    const replacing = state.busy && !!state.replaceable;
    if (state.busy && !replacing) {
      showError(
        "Another recording or transcription is already in progress for this case.",
        dispatch
      );
      return false;
    }
    reservationRef.current = {
      expectedId: state.submission_id,
      overwrite: hasExistingFullTranscript || replacing,
      replaceId: replacing ? state.submission_id : null,
    };
    if (!reservationRef.current.overwrite) {
      return Promise.resolve(true);
    }
    setIsReplacingPending(replacing);

    return new Promise<boolean>((resolve) => {
      confirmationResolverRef.current = resolve;
      setIsOverwriteWarningOpen(true);
    });
  }, [hasExistingFullTranscript, caseNumber, dispatch]);

  const requestRecoveryCheck = useRecordingDock((s) => s.requestRecoveryCheck);

  const handleTranscribeClick = useCallback(async () => {
    if (isTranscribing || isLoading) {
      return;
    }

    // A new recording deletes this case's earlier unsent one (MDRS-551), so
    // bring its recovery prompt back for Send / Discard instead of starting.
    if (caseNumber && (await hasUnsentRecording(caseNumber))) {
      requestRecoveryCheck();
      return;
    }

    const shouldProceed = await requestOverwriteConfirmation();
    if (!shouldProceed) {
      return;
    }

    setIsConsentOpen(true);
  }, [
    caseNumber,
    isLoading,
    isTranscribing,
    requestOverwriteConfirmation,
    requestRecoveryCheck,
  ]);

  const handleOverwriteConfirm = useCallback(() => {
    setIsOverwriteWarningOpen(false);
    resolvePendingConfirmation(true);
  }, [resolvePendingConfirmation]);

  const handleOverwriteCancel = useCallback(() => {
    setIsOverwriteWarningOpen(false);
    resolvePendingConfirmation(false);
  }, [resolvePendingConfirmation]);

  // Best-effort: a failed refresh clears the reservation so the next start re-checks.
  const refreshReservation = useCallback(async () => {
    try {
      const state = await callApi(
        `transcription/submissions/state/${caseNumber}`,
        "GET"
      );
      const previous = reservationRef.current;
      // Consent was for the submission the user saw; a different one needs asking again.
      const same = previous?.expectedId === state.submission_id;
      reservationRef.current = {
        expectedId: state.submission_id,
        overwrite: (same && previous?.overwrite) ?? false,
        replaceId: (same && previous?.replaceId) || null,
      };
    } catch {
      reservationRef.current = null;
    }
  }, [caseNumber]);

  // Start the session from the consent gate. On success the live view takes
  // over the content panel and the modal closes; on failure the hook returns to
  // idle and we keep the modal open so the user can retry / back out.
  const handleStartRecording = useCallback(
    async (
      speakerHints: SpeakerHint[],
      mode: SessionMode,
      callAudioTrack?: MediaStreamTrack,
      micDeviceId?: string
    ) => {
      // caseNumber intentionally omitted: `session` here is
      // AudioTranscriberPage's wrapped RecordingSession, whose own `start`
      // already injects the page's caseNumber unconditionally
      // (AudioTranscriberPage.tsx) — passing it again here would just be
      // overwritten there, so there's nothing to wire through.
      if (!caseNumber) return;
      if (!reservationRef.current) {
        showError("Close this window and start the session again.", dispatch);
        return;
      }
      await claimRecording();
      try {
        await prepareRecording(
          caseNumber,
          reservationRef.current.expectedId,
          reservationRef.current.overwrite,
          undefined,
          reservationRef.current.replaceId
        );
        const started = await session.start(speakerHints, {
          initialNotes,
          mode,
          callAudioTrack,
          micDeviceId,
        });
        if (!started) {
          await discardRecording();
          await refreshReservation();
          return;
        }
        setIsConsentOpen(false);
      } catch (error) {
        await discardRecording().catch(() => {});
        await refreshReservation();
        throw error;
      }
    },
    [session, caseNumber, initialNotes, refreshReservation, dispatch]
  );

  const handleConsentClose = useCallback(() => {
    setIsConsentOpen(false);
  }, []);

  // The header variant is gated by the active section; the empty-state variant
  // always renders (it owns the no-content panel).
  if (!isEmptyState && !shouldShowButtons) {
    return null;
  }

  const withBlockedTooltip = (control: ReactNode) =>
    blockedReason ? (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <span tabIndex={0} className="inline-flex">
            {control}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{blockedReason}</TooltipContent>
      </Tooltip>
    ) : (
      control
    );

  const blockedPill = blockedReason ? (
    <Badge variant="warning" role="status">
      {blockedReason}
    </Badge>
  ) : null;

  const uploader = (
    <AudioUploader
      disabled={isTranscribing || isLoading || sessionInProgress || browserBusy}
      caseNumber={caseNumber || ""}
      examinee_name={caseData?.examinee_name || ""}
      onRequestOverwriteConfirm={requestOverwriteConfirmation}
      onPrepareUpload={async () => {
        if (!caseNumber || !reservationRef.current)
          throw new Error("Refresh the assessment before uploading.");
        await claimRecording();
        try {
          return await prepareRecording(
            caseNumber,
            reservationRef.current.expectedId,
            reservationRef.current.overwrite,
            undefined,
            reservationRef.current.replaceId
          );
        } catch (error) {
          releaseRecording();
          throw error;
        }
      }}
    />
  );

  const startButton = (
    <Button
      // Always sm — header and empty-state must render the same button size
      // (previously "default" in the empty state, mismatching Upload Recording
      // and the header's own "sm" Start Session button).
      size="sm"
      // Disable while a session is connecting/live/stopping so a second
      // session can't be started over the in-panel one. Show the current
      // state on the label + spinner so the button reflects what's happening.
      disabled={isTranscribing || isLoading || sessionInProgress || browserBusy}
      isLoading={session.isConnecting}
      onClick={handleTranscribeClick}
      className="flex items-center gap-1.5"
    >
      {/* The SVG ships a hardcoded primary-blue fill; override it so the
          icon follows the button's foreground (white on the primary fill).
          size-4 to match Upload Recording's icon — size-5 made this button
          taller than its sibling despite both using size="sm". */}
      {!session.isConnecting && (
        <MicIcon className="size-4 [&_path]:fill-primary-foreground" />
      )}
      {session.isConnecting
        ? "Connecting…"
        : sessionInProgress
          ? "Session in progress"
          : "Start Session"}
    </Button>
  );

  return (
    <>
      {isEmptyState ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-10 pb-[60px] pt-20 text-center">
          <div className="flex h-[52px] items-center justify-center rounded-full bg-primary-50 px-3.5">
            <MicIcon className="size-6 [&_path]:fill-primary" />
          </div>
          <div className="flex flex-col items-center gap-2.5">
            <h2 className="text-lg font-semibold text-foreground">
              No content available yet
            </h2>
            <p className="text-[13px] text-foreground-muted">
              Record your session live or upload an existing recording to generate
              AI-powered summaries for each section.
            </p>
          </div>
          {blockedPill}
          {withBlockedTooltip(
            <div className="flex items-center gap-3">
              {uploader}
              {startButton}
            </div>
          )}
          <p className="text-[11px] text-foreground-muted">
            Supported formats: MP3, MP4, WAV, M4A · Max size 500MB
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          {blockedPill}
          {withBlockedTooltip(
            <div className="flex items-center gap-2">
              {uploader}
              {startButton}
            </div>
          )}
        </div>
      )}

      {/* Consent gate — a modal (MDRS-115, kept in Phase 3). The LIVE session
          renders in the content panel once Start Recording is confirmed. */}
      <Dialog open={isConsentOpen} onOpenChange={setIsConsentOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-w-lg"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <AudioRecorder
            handleClose={handleConsentClose}
            examinee_name={caseData?.examinee_name || ""}
            caseNumber={caseNumber || ""}
            callAudioShare={callAudioShare}
            onStartRecording={handleStartRecording}
            isConnecting={session.isConnecting}
          />
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        type="warning"
        title={
          isReplacingPending
            ? "Replace the transcription in progress?"
            : "Overwrite the full transcript?"
        }
        description={
          isReplacingPending
            ? "The last recording for this case is still being transcribed. Starting a new session or uploading a new recording will discard it."
            : "This case already has a Full Transcript. Starting a new transcription or uploading a new recording will overwrite the existing transcript for this case."
        }
        body={
          <p className="text-muted-foreground text-sm">
            If you want to add additional commentary, please update the individual
            modules or add it under &quot;Assessment notes&quot;.
          </p>
        }
        confirmButtonText={
          isReplacingPending ? "Replace transcription" : "Overwrite transcript"
        }
        cancelButtonText="Cancel"
        onConfirm={handleOverwriteConfirm}
        onCancel={handleOverwriteCancel}
        open={isOverwriteWarningOpen}
      />
    </>
  );
};
